import React, { useContext, useCallback, useRef, useState, useEffect } from 'react';
import { TableContext } from './context/TableContext';
import { usePreferences } from '../../../hooks/usePreferences';
import HeaderContextMenu from './HeaderContextMenu';
import Checkbox from '../Checkbox';

// Move outside component to prevent recreation
const HEADER_CLASSES = 'px-4 py-2 transition-colors duration-150';

const ColumnResizer = ({ onResize, onDoubleClick, currentWidth, isAnyDragging, setIsAnyDragging, columnField, activeDragColumn, setActiveDragColumn, setJustFinishedResizing, className }) => {
  const startResizing = useCallback((e) => {
    e.preventDefault(); // Prevent drag start
    e.stopPropagation(); // Prevent header click (sorting)
    const startX = e.pageX;
    const startWidth = currentWidth;
    
    const handleMouseMove = (moveEvent) => {
      moveEvent.preventDefault();
      moveEvent.stopPropagation();
      const diff = moveEvent.pageX - startX;
      const newWidth = startWidth + diff;
      onResize(newWidth);
    };
    
    const handleMouseUp = () => {
      setIsAnyDragging(false);
      setActiveDragColumn(null);
      setJustFinishedResizing(true);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    setIsAnyDragging(true);
    setActiveDragColumn(columnField);
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [onResize, currentWidth, columnField, setActiveDragColumn, setJustFinishedResizing, setIsAnyDragging]);

  const isActive = activeDragColumn === columnField;
  const showHover = !isAnyDragging || isActive;

  return (
    <div
      className={`absolute right-0 top-0 w-1 h-full cursor-col-resize ${
        isActive ? 'bg-indigo-500' : showHover ? 'hover:bg-indigo-500 group-hover:bg-indigo-400' : ''
      } ${className || ''}`}
      onMouseDown={startResizing}
      onDoubleClick={onDoubleClick}
      onClick={e => e.stopPropagation()}
      onDragStart={e => e.preventDefault()}
      draggable={false}
      data-resizer="true"
    />
  );
};

const TableHeaderCell = React.memo(({ 
  column, 
  onSort, 
  getSortInfo, 
  renderSortIndicator, 
  tableRef, 
  isAnyDragging, 
  setIsAnyDragging,
  activeDragColumn,
  setActiveDragColumn,
  onContextMenu,
  isPinned,
  pinnedOffset,
  onDragStart,
  onDragOver,
  onDragEnter,
  onDragLeave,
  onDragEnd
}) => {
  const { columnWidths, handleColumnResize, handleColumnAutoResize } = useContext(TableContext);
  const [justFinishedResizing, setJustFinishedResizing] = useState(false);
  const startWidthRef = useRef(columnWidths[column.field] || 150);
  
  const handleResize = useCallback((newWidth) => {
    const finalWidth = Math.max(newWidth, 50);
    handleColumnResize(column.field, finalWidth);
  }, [column.field, handleColumnResize]);

  const handleDoubleClick = useCallback((e) => {
    e.stopPropagation();
    handleColumnAutoResize(column.field, tableRef);
    startWidthRef.current = columnWidths[column.field] || 150;
  }, [column.field, handleColumnAutoResize, tableRef, columnWidths]);

  useEffect(() => {
    if (justFinishedResizing) {
      const timer = setTimeout(() => {
        setJustFinishedResizing(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [justFinishedResizing]);

  const handleClick = useCallback(() => {
    if (justFinishedResizing) {
      return;
    }
    onSort(column.field);
  }, [column.field, onSort, justFinishedResizing]);

  const handleContextMenu = useCallback((e) => {
    e.preventDefault();
    onContextMenu(e, column);
  }, [onContextMenu, column]);

  const width = columnWidths[column.field] || 150;
  return (
    <th
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      data-field={column.field}
      style={{ 
        width: `${width}px`,
        minWidth: `${width}px`,
        maxWidth: `${width}px`,
        left: isPinned ? `${pinnedOffset}px` : 'auto',
        flexShrink: 0, // Prevent Safari from redistributing space
        flexGrow: 0 // Prevent Safari from redistributing space
      }}
      className={`${HEADER_CLASSES} text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-indigo-100 relative group select-none transform-gpu ${isPinned ? 'sticky z-[6] shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] bg-indigo-75' : 'bg-indigo-50'}`}
      draggable={true}
      onDragStart={(e) => onDragStart(e, column)}
      onDragOver={(e) => onDragOver(e, column)}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragEnd={onDragEnd}
    >
      <div className="flex items-center h-[22px]">
        <div className="flex items-center flex-grow h-full overflow-hidden">
          <span className="whitespace-nowrap text-ellipsis overflow-hidden block w-full">{column.headerName}</span>
        </div>
        <div className="flex-shrink-0 ml-1 h-full flex items-center">
          <span>{renderSortIndicator(getSortInfo(column.field))}</span>
        </div>
      </div>
      <ColumnResizer 
        onResize={handleResize} 
        onDoubleClick={handleDoubleClick}
        currentWidth={width}
        isAnyDragging={isAnyDragging}
        setIsAnyDragging={setIsAnyDragging}
        columnField={column.field}
        activeDragColumn={activeDragColumn}
        setActiveDragColumn={setActiveDragColumn}
        setJustFinishedResizing={setJustFinishedResizing}
        className="resizer"
      />
    </th>
  );
});

const TableHeaderComponent = ({
  columns,
  selectedItems,
  totalItems,
  onHeaderCheckboxChange,
  tableRef,
  innerRef
}) => {
  const { 
    sortConfig, 
    handleSort, 
    columnWidths, 
    handleColumnResize, 
    handleColumnAutoResize,
    handleColumnOrderChange,
    handleColumnReorder,
    handleColumnPin,
    isColumnPinned
  } = useContext(TableContext);
  const [isAnyDragging, setIsAnyDragging] = useState(false);
  const [activeDragColumn, setActiveDragColumn] = useState(null);
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [reorderedColumns, setReorderedColumns] = useState(columns);
  const [isResizing, setIsResizing] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const { preferences, updatePreferences } = usePreferences();

  const handleContextMenu = useCallback((e, column) => {
    e.preventDefault();
    
    if (contextMenu?.column.field === column.field) {
      setContextMenu(null);
      return;
    }
    
    const headerRect = e.currentTarget.getBoundingClientRect();
    setContextMenu({
      x: headerRect.left,
      y: headerRect.bottom,
      column
    });
  }, [contextMenu]);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  useEffect(() => {
    if (contextMenu) {
      const handleClickOutside = (e) => {
        if (!e.target.closest('.context-menu')) {
          handleCloseContextMenu();
        }
      };
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [contextMenu, handleCloseContextMenu]);

  const handleDragStart = useCallback((e, column) => {
    if (isResizing) {
      e.preventDefault();
      return;
    }

    e.dataTransfer.effectAllowed = 'move';
    setDraggedColumn(column);
    
    const dragImage = e.target.cloneNode(true);
    dragImage.style.opacity = '0.5';
    dragImage.style.position = 'absolute';
    dragImage.style.top = '-1000px';
    document.body.appendChild(dragImage);
    e.dataTransfer.setDragImage(dragImage, 0, 0);
    setTimeout(() => document.body.removeChild(dragImage), 0);
  }, [isResizing]);

  useEffect(() => {
    setReorderedColumns(columns);
  }, [columns]);

  const scrollSpeed = useRef(0);
  const scrollInterval = useRef(null);

  // Handle auto-scrolling during drag
  const handleDrag = useCallback((e) => {
    if (!draggedColumn || !tableRef.current) return;

    const container = tableRef.current;
    const containerRect = container.getBoundingClientRect();
    const SCROLL_THRESHOLD = 100;
    const MAX_SCROLL_SPEED = 15;

    // Calculate total width of pinned columns
    let pinnedWidth = onHeaderCheckboxChange ? 50 : 0; // checkbox column if present
    reorderedColumns.some(col => {
      if (isColumnPinned(col.field)) {
        pinnedWidth += (columnWidths[col.field] || 150);
      } else {
        return true; // Stop once we hit first unpinned column
      }
      return false;
    });

    // Adjust left boundary to account for pinned columns
    const effectiveLeft = containerRect.left + pinnedWidth;

    if (e.clientX < effectiveLeft + SCROLL_THRESHOLD) {
      scrollSpeed.current = -MAX_SCROLL_SPEED * (1 - (e.clientX - effectiveLeft) / SCROLL_THRESHOLD);
    } else if (e.clientX > containerRect.right - SCROLL_THRESHOLD) {
      scrollSpeed.current = MAX_SCROLL_SPEED * (1 - (containerRect.right - e.clientX) / SCROLL_THRESHOLD);
    } else {
      scrollSpeed.current = 0;
    }

    if (scrollSpeed.current !== 0) {
      if (!scrollInterval.current) {
        scrollInterval.current = setInterval(() => {
          if (container) {
            container.scrollLeft += scrollSpeed.current;
          }
        }, 16);
      }
    } else if (scrollInterval.current) {
      clearInterval(scrollInterval.current);
      scrollInterval.current = null;
    }
  }, [draggedColumn]);

  const handleDragOver = useCallback((e, column) => {
    if (isResizing) return;
    
    e.preventDefault();
    handleDrag(e);

    if (draggedColumn && draggedColumn.field !== column.field) {
      const rect = e.currentTarget.getBoundingClientRect();
      const isOnLeftHalf = e.clientX - rect.left < rect.width / 2;
      const position = isOnLeftHalf ? 'before' : 'after';
      
      if (dragOverColumn?.field !== column.field || dragOverColumn?.position !== position) {
        const oldIndex = reorderedColumns.findIndex(col => col.field === draggedColumn.field);
        let newIndex = reorderedColumns.findIndex(col => col.field === column.field);
        
        if (oldIndex !== -1 && newIndex !== -1) {
          if (position === 'after') {
            newIndex += 1;
          }
          
          const newColumns = [...reorderedColumns];
          newColumns.splice(oldIndex, 1);
          newIndex = newIndex > oldIndex ? newIndex - 1 : newIndex;
          newColumns.splice(newIndex, 0, draggedColumn);
          
          let foundUnpinned = false;
          for (const col of newColumns) {
            if (!isColumnPinned(col.field)) {
              foundUnpinned = true;
            } else if (foundUnpinned) {
              return;
            }
          }
          
          setDragOverColumn({ field: column.field, position });
          setReorderedColumns(newColumns);
          handleColumnReorder(newColumns.map(col => col.field));
        }
      }
    }
  }, [draggedColumn, isResizing, reorderedColumns, dragOverColumn]);

  useEffect(() => {
    if (draggedColumn) {
      document.addEventListener('dragover', handleDrag);
      return () => document.removeEventListener('dragover', handleDrag);
    }
  }, [draggedColumn, handleDrag]);

  const handleDragEnter = useCallback((e) => {
    if (isResizing) return;
    e.preventDefault();
    e.currentTarget.classList.add('bg-indigo-100');
  }, [isResizing]);

  const handleDragLeave = useCallback((e) => {
    if (isResizing) return;
    e.preventDefault();
    e.currentTarget.classList.remove('bg-indigo-100');
  }, [isResizing]);

  const handleDragEnd = useCallback(() => {
    if (isResizing) return;
    
    if (scrollInterval.current) {
      clearInterval(scrollInterval.current);
      scrollInterval.current = null;
    }
    
    handleColumnOrderChange(reorderedColumns.map(col => col.field));
    setDraggedColumn(null);
    setDragOverColumn(null);
    scrollSpeed.current = 0;
  }, [reorderedColumns, handleColumnOrderChange, isResizing]);

  useEffect(() => {
    setIsResizing(isAnyDragging);
  }, [isAnyDragging]);

  // Cleanup scroll interval on unmount
  useEffect(() => {
    return () => {
      if (scrollInterval.current) {
        clearInterval(scrollInterval.current);
        scrollInterval.current = null;
      }
    };
  }, []);

  const getSortInfo = useCallback((column) => {
    if (!Array.isArray(sortConfig)) return { icon: '' };
    
    const sortIndex = sortConfig.findIndex(c => c.column === column);
    if (sortIndex === -1) return { icon: '' };
    
    const { order } = sortConfig[sortIndex];
    const icon = order === 'ASC' ? '↑' : '↓';
    const number = sortConfig.length > 1 ? sortIndex + 1 : '';
    
    return { icon, number };
  }, [sortConfig]);

  const renderSortIndicator = useCallback(({ icon, number }) => {
    if (!icon) return null;
    if (!number) return icon;
    return (
      <span className="inline-flex items-center">
        {icon}
        <span className="ml-0.5 text-[10px] text-gray-400">{number}</span>
      </span>
    );
  }, []);

  const getPinnedOffset = useCallback((columnField) => {
    let offset = onHeaderCheckboxChange ? 50 : 0;
    for (const col of reorderedColumns) {
      if (col.field === columnField) break;
      if (isColumnPinned(col.field)) {
        offset += (columnWidths[col.field] || 150);
      }
    }
    return offset;
  }, [reorderedColumns, isColumnPinned, columnWidths, onHeaderCheckboxChange]);

  return (
    <>
      <thead ref={innerRef} className="sticky top-0 z-[5] shadow-sm">
        <tr>
          {onHeaderCheckboxChange && (
            <th className={`${HEADER_CLASSES} sticky left-0 z-[6] bg-indigo-75`} draggable={false}>
              <div className="h-full flex items-center">
                <Checkbox
                  id="header-select-all"
                  name="header-select-all"
                  checked={selectedItems.size === totalItems && totalItems > 0}
                  onChange={onHeaderCheckboxChange}
                />
              </div>
            </th>
          )}
          {reorderedColumns.map(column => (
            <TableHeaderCell
              key={column.field}
              column={column}
              onSort={handleSort}
              getSortInfo={getSortInfo}
              renderSortIndicator={renderSortIndicator}
              tableRef={tableRef}
              isAnyDragging={isAnyDragging}
              setIsAnyDragging={setIsAnyDragging}
              activeDragColumn={activeDragColumn}
              setActiveDragColumn={setActiveDragColumn}
              onContextMenu={handleContextMenu}
              isPinned={isColumnPinned(column.field)}
              pinnedOffset={getPinnedOffset(column.field)}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragEnd={handleDragEnd}
            />
          ))}
        </tr>
      </thead>
      {contextMenu && (
        <HeaderContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={handleCloseContextMenu}
          onPin={() => handleColumnPin(contextMenu.column.field)}
          isPinned={isColumnPinned(contextMenu.column.field)}
        />
      )}
    </>
  );
};

const TableHeader = React.forwardRef((props, ref) => (
  <TableHeaderComponent {...props} innerRef={ref} />
));

export default React.memo(TableHeader);
